import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  children: React.ReactNode;
  onClose?: () => void;
  /** When false, backdrop click does nothing. Default true when onClose is set. */
  closeOnBackdrop?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Nome acessível do diálogo (lido por leitor de tela). */
  label?: string;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Viewport-safe modal shell: portals to document.body so header/backdrop-filter
 * ancestors cannot trap position:fixed or steal stacking (header z-index 1000).
 * Também cuida de foco: entra no diálogo, prende o Tab e devolve o foco ao fechar.
 */
export default function ModalOverlay({
  children,
  onClose,
  closeOnBackdrop = true,
  className = 'modal-overlay',
  style,
  label,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  // onClose costuma vir inline: guardar em ref evita refazer o efeito (e roubar o foco) a cada render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const scroller = document.querySelector('.content-wrapper') as HTMLElement | null;
    const prevOverflow = scroller?.style.overflow ?? '';
    if (scroller) scroller.style.overflow = 'hidden';

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Foco inicial: respeita um autoFocus já aplicado; senão vai ao primeiro campo/botão.
    const root = rootRef.current;
    if (root && !root.contains(document.activeElement)) {
      const first = root.querySelector<HTMLElement>(FOCUSABLE);
      (first || root).focus({ preventScroll: true });
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCloseRef.current) {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !rootRef.current) return;
      const items = Array.from(rootRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (!items.length) {
        e.preventDefault();
        rootRef.current.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === rootRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      if (scroller) scroller.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={rootRef}
      className={className}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      style={style}
      onClick={() => {
        if (closeOnBackdrop && onClose) onClose();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
